import React, { useState, useEffect, useCallback } from "react";
import { 
    Table, Row, Col, Input, Select, DatePicker, 
    Tag, Card, Tooltip
} from "antd";
import type { SelectProps } from "antd";
import { getUserActivityLogs } from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import { ACTIVITY_TYPES } from "@/ts/common-types";
import { convertIsoDate } from "@/ts/utils";
import { useUserTimezone } from "@/hooks/AllCustomHooks";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/ts/constants";
import { debounce } from "underscore";

const { RangePicker } = DatePicker;

interface UserActivityLogsProps {
    userId: number;
}

interface ActivityLog {
    id: number;
    actor_name: string;
    actor_email: string;
    actor_user_id: number | null;
    activity_type: string;
    activity_subtype: string;
    change_description: string;
    created_at: string;
}

interface ActivityLogFilters {
    targetUserId: string;
    actorUserId?: string;
    activityTypes: string[];
    startDate: string | null;
    endDate: string | null;
    search: string;
}

const UserActivityLogs: React.FC<UserActivityLogsProps> = ({ userId }) => {
    const currentTimeZone = useUserTimezone();
    
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
    });
    const [actorOptions, setActorOptions] = useState<SelectProps["options"]>([]);
    
    const [filters, setFilters] = useState<ActivityLogFilters>({
        targetUserId: userId.toString(),
        actorUserId: undefined,
        activityTypes: [],
        startDate: null,
        endDate: null,
        search: "",
    });

    const fetchLogs = useCallback(
        async (
            page = 1,
            pageSize = pagination.pageSize,
            currentFilters: ActivityLogFilters = filters
        ) => {
            setLoading(true);
            try {
                const result = await getUserActivityLogs(
                    currentFilters,
                    page,
                    pageSize
                );
                const fetchedLogs: ActivityLog[] = result.logs || [];
                setLogs(fetchedLogs);
                setPagination({
                    current: page,
                    pageSize,
                    total: result.pagination?.total || 0,
                });

                const options = fetchedLogs.reduce<
                    NonNullable<SelectProps["options"]>
                >((acc, log) => {
                    if (!log.actor_user_id) {
                        return acc;
                    }
                    const value = String(log.actor_user_id);
                    const exists = acc?.some(
                        (option) => option?.value === value
                    );
                    if (!exists) {
                        acc?.push({
                            label: `${log.actor_name} (${log.actor_email})`,
                            value,
                        });
                    }
                    return acc;
                }, []);

                setActorOptions(options);
            } catch (error) {
                showToast("danger", "Failed to load activity logs");
            } finally {
                setLoading(false);
            }
        },
        [filters, pagination.pageSize]
    );

    useEffect(() => {
        if (!userId) {
            return;
        }

        const defaultFilters: ActivityLogFilters = {
            targetUserId: userId.toString(),
            actorUserId: undefined,
            activityTypes: [],
            startDate: null,
            endDate: null,
            search: "",
        };

        setFilters(defaultFilters);
        setActorOptions([]);
        fetchLogs(1, pagination.pageSize, defaultFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const debouncedSearch = debounce((value: string) => {
        const newFilters = { ...filters, search: value };
        setFilters(newFilters);
        fetchLogs(1, pagination.pageSize, newFilters);
    }, 500);

    const handleFilterChange = (key: keyof ActivityLogFilters, value: any) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        fetchLogs(1, pagination.pageSize, newFilters);
    };

    const handleDateRangeChange = (dates: any) => {
        const newFilters: ActivityLogFilters = {
            ...filters,
            startDate: dates?.[0]?.toISOString(),
            endDate: dates?.[1]?.toISOString(),
        };
        setFilters(newFilters);
        fetchLogs(1, pagination.pageSize, newFilters);
    };

    const columns = [
        {
            title: "Date/Time",
            dataIndex: "created_at",
            key: "created_at",
            width: 200,
            render: (text: string) => (
                <div>
                    <div>{convertIsoDate(text, currentTimeZone)}</div>
                    <div className="small text-muted">{convertIsoDate(text)} - UTC</div>
                </div>
            ),
        },
        {
            title: "Change Description",
            dataIndex: "change_description",
            key: "change_description",
            width: 400,
            render: (text: string, record: any) => {
                const metadata = record.metadata || {};
                const targetUserName = metadata.target_user_name || 
                                      (record.target_entity_type === "USER" ? record.target_entity_name : null);
                const targetUserId = metadata.target_user_id || 
                                   (record.target_entity_type === "USER" ? record.target_entity_id : null);
                
                let contextInfo: React.ReactNode[] = [];
                if (targetUserName || targetUserId) {
                    contextInfo.push(
                        <span key="user" className="small text-muted d-block mt-1">
                            User: {targetUserName || 'N/A'}{targetUserId ? ` (ID: ${targetUserId})` : ''}
                        </span>
                    );
                }
                
                const maxDescriptionLength = 60; 
                const truncatedDescription = text && text.length > maxDescriptionLength 
                    ? text.substring(0, maxDescriptionLength) + '...'
                    : text;
                
                return (
                    <div style={{ 
                        wordWrap: 'break-word', 
                        wordBreak: 'break-word', 
                        whiteSpace: 'normal',
                        maxWidth: '400px'
                    }}>
                        {text && text.length > maxDescriptionLength ? (
                            <Tooltip title={text} placement="top">
                                <div style={{ 
                                    whiteSpace: 'normal', 
                                    wordWrap: 'break-word', 
                                    wordBreak: 'break-word',
                                    cursor: 'help',
                                    textDecoration: 'none'
                                }}>
                                    {truncatedDescription}
                                </div>
                            </Tooltip>
                        ) : (
                            <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', wordBreak: 'break-word' }}>
                                {text}
                            </div>
                        )}
                        {contextInfo}
                    </div>
                );
            },
        },
        {
            title: "Changed By",
            key: "actor",
            width: 250,
            render: (_: any, record: any) => (
                <div>
                    <div>{record.actor_name || "System"}</div>
                    <div className="small text-muted">{record.actor_email || "system@reviniti.com"}</div>
                </div>
            ),
        },
        {
            title: "Activity Type",
            dataIndex: "activity_type",
            key: "activity_type",
            width: 200,
            render: (text: string, record: any) => (
                <div>
                    <Tag color="blue">{text}</Tag>
                    {record.activity_subtype && (
                        <div className="small text-muted">{record.activity_subtype}</div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div>
            <Card>
                <Row gutter={[16, 16]} className="mb-3">
                    <Col span={8}>
                        <Input
                            placeholder="Search descriptions..."
                            onChange={(e) => debouncedSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            mode="multiple"
                            placeholder="Activity Types"
                            style={{ width: "100%" }}
                            value={filters.activityTypes}
                            onChange={(value) =>
                                handleFilterChange("activityTypes", value)
                            }
                            allowClear
                            options={ACTIVITY_TYPES.map((type) => ({
                                label: type,
                                value: type,
                            }))}
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            allowClear
                            placeholder="Changed By"
                            style={{ width: "100%" }}
                            value={filters.actorUserId}
                            options={actorOptions}
                            onChange={(value) =>
                                handleFilterChange(
                                    "actorUserId",
                                    value || undefined
                                )
                            }
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} className="mb-4">
                    <Col span={12}>
                        <RangePicker
                            style={{ width: "100%" }}
                            onChange={handleDateRangeChange}
                            placeholder={["Start Date", "End Date"]}
                        />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={logs}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) => fetchLogs(page, pageSize),
                        onShowSizeChange: (current, size) => fetchLogs(1, size),
                        showSizeChanger: true,
                        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    scroll={{ 
                        y: 600
                    }}
                    style={{ 
                        tableLayout: 'fixed',
                        width: '100%',
                        maxHeight: '600px'
                    }}
                    components={{
                        body: {
                            cell: (props: any) => (
                                <td 
                                    {...props} 
                                    style={{ 
                                        ...props.style,
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        verticalAlign: 'top',
                                        padding: '12px 8px',
                                        overflowWrap: 'break-word'
                                    }} 
                                />
                            ),
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export default UserActivityLogs;
